import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { fetchProducts } from "./api";

const StoreContext = createContext(null);
export const FREE_SHIP = 75;

function useLocal(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : initial;
      return Array.isArray(parsed) ? parsed : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function StoreProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useLocal("ldm_cart", []);
  const [wishlist, setWishlist] = useLocal("ldm_wishlist", []);

  useEffect(() => {
    fetchProducts().then((p) => {
      setProducts(p);
      setLoading(false);
    });
  }, []);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const addToCart = (product, { size = null, color = null, quantity = 1 } = {}) => {
    const key = `${product.id}|${size ?? ""}|${color ?? ""}`;
    setCart((c) => {
      const found = c.find((l) => l.key === key);
      if (found) return c.map((l) => (l.key === key ? { ...l, quantity: Math.min(20, l.quantity + quantity) } : l));
      return [...c, { key, product_id: product.id, size, color, quantity }];
    });
    toast.success(`${product.name} added to your bag`);
  };
  const updateQty = (key, quantity) =>
    setCart((c) => c.map((l) => (l.key === key ? { ...l, quantity: Math.max(1, Math.min(20, quantity)) } : l)));
  const removeLine = (key) => setCart((c) => c.filter((l) => l.key !== key));
  const clearCart = () => setCart([]);

  const isWished = (id) => wishlist.includes(id);
  const toggleWish = (product) => {
    const had = wishlist.includes(product.id);
    setWishlist((w) => (had ? w.filter((x) => x !== product.id) : [...w, product.id]));
    toast(had ? "Removed from wishlist" : `${product.name} saved to your wishlist`);
  };
  const clearWishlist = () => setWishlist([]);

  const lines = cart.filter((l) => byId[l.product_id]).map((l) => ({ ...l, product: byId[l.product_id] }));
  const subtotal = lines.reduce((s, l) => s + l.product.price * l.quantity, 0);
  const cartCount = cart.reduce((s, l) => s + l.quantity, 0);

  const value = {
    products, loading, byId, cart, lines, subtotal, cartCount, addToCart, updateQty, removeLine, clearCart,
    wishlist, isWished, toggleWish, clearWishlist,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
